import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Link2 } from "lucide-react";
import { Button } from "../../../components/ui/Button/Button";
import { getOAuthStatus, getOAuthConnectURL } from "../../../api/analytics";
import type { Website } from "../types";
import { sourceLabel } from "../types";
import { analyticsKey } from "../queryKeys";
import type { ViewProps } from "./OverviewView";
import { BindingDialog } from "../components/AddWebsiteDialog";
import { SourceBadge } from "../components/States";
import { errorMessage } from "../format";
import { dateLabel } from "../dateRange";
import styles from "../Analytics.module.css";
export default function ConnectionsView({
  website,
  scope,
  id,
  context,
  canManage,
}: { website: Website; canManage: boolean } & ViewProps) {
  const [params] = useSearchParams();
  const requested = params.get("connect");
  const [editing, setEditing] = useState<"ga4" | "gsc" | null>(
      canManage && (requested === "ga4" || requested === "gsc")
        ? requested
        : null,
    ),
    [connecting, setConnecting] = useState(false),
    [error, setError] = useState<string | null>(null);
  const account = useQuery({
    queryKey: analyticsKey(scope, "oauth"),
    queryFn: getOAuthStatus,
    staleTime: 60000,
  });
  const connect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const response = await getOAuthConnectURL();
      sessionStorage.setItem(
        `analytics-oauth-return:${scope.userId}`,
        JSON.stringify({
          state: response.state,
          path: window.location.pathname + window.location.search,
          organizationId: scope.organizationId,
        }),
      );
      window.location.assign(response.authorization_url);
    } catch (e) {
      setError(errorMessage(e));
      setConnecting(false);
    }
  };
  return (
    <div className={styles.stack}>
      <section className={styles.panel}>
        <div className={styles.panelHeading}>
          <div>
            <h2>
              <Link2 size={18} />
              Website connections
            </h2>
            <p>Review exactly what each report includes.</p>
          </div>
        </div>
        <div className={styles.scopeSummary}>
          <strong>
            {website.scope_value || "Website address not supplied"}
          </strong>
          <span>
            {website.mapping_status === "confirmed" ? (
              <>
                <CheckCircle2 size={15} />
                Source mapping confirmed
              </>
            ) : (
              "Source mapping needs review"
            )}
          </span>
          <p className={styles.caption}>
            A GA4 property can cover several hosts. Search Console can cover a
            domain or an exact URL prefix. Similar names do not establish
            equivalent scope.
          </p>
        </div>
        <div className={styles.connectionGrid}>
          {(["gsc", "ga4"] as const).map((source) => {
            const state = context.sources[source];
            const sourceId =
              source === "ga4"
                ? website.primary_ga4_property_id
                : website.primary_gsc_site_id;
            return (
              <article className={styles.connectionCard} key={source}>
                <div className={styles.panelHeading}>
                  <h3>{sourceLabel[source]}</h3>
                  <SourceBadge source={source} state={state} />
                </div>
                <dl className={styles.connectionDetails}>
                  <dt>Selected resource</dt>
                  <dd>
                    {sourceId
                      ? state.resource_scope ||
                        "Resource details not yet available"
                      : "Not linked to this website"}
                  </dd>
                  <dt>Account</dt>
                  <dd>
                    {state.account_email || "Account details unavailable"}
                  </dd>
                  <dt>Last sync succeeded</dt>
                  <dd>
                    {state.last_successful_sync_at
                      ? new Date(state.last_successful_sync_at).toLocaleString()
                      : "No successful sync recorded"}
                  </dd>
                  <dt>Data available through</dt>
                  <dd>
                    {state.data_through
                      ? dateLabel(state.data_through)
                      : "Coverage not established"}
                  </dd>
                  <dt>Report coverage</dt>
                  <dd>
                    {state.data_status === "ready"
                      ? "Available for the selected period"
                      : state.data_status === "partial"
                        ? "Some requested history is missing"
                        : state.data_status === "empty"
                          ? "Successful report with no activity"
                          : "Not available for this period"}
                  </dd>
                  <dt>Reporting timezone</dt>
                  <dd>{state.reporting_timezone || "Not established"}</dd>
                  <dt>Refresh activity</dt>
                  <dd>
                    {state.sync_status === "idle"
                      ? "No refresh running"
                      : state.sync_status.replace(/_/g, " ")}
                  </dd>
                </dl>
                {sourceId && (
                  <details className={styles.caption}>
                    <summary>Resource identifier</summary>
                    <code>{sourceId}</code>
                  </details>
                )}
                {canManage ? (
                  <Button
                    variant="secondary"
                    onClick={() => setEditing(source)}
                  >
                    {sourceId
                      ? `Manage ${source === "ga4" ? "GA4 property" : "Search Console site"}`
                      : `Link ${source === "ga4" ? "GA4 property" : "Search Console site"}`}
                  </Button>
                ) : (
                  <p className={styles.caption}>
                    Ask a workspace owner to change connections.
                  </p>
                )}
                {(state.connection_status === "reauth_required" ||
                  state.connection_status === "permission_denied") &&
                  canManage && (
                    <Button
                      variant="ghost"
                      loading={connecting}
                      onClick={connect}
                    >
                      Reconnect Google account
                    </Button>
                  )}
              </article>
            );
          })}
        </div>
        <p className={styles.caption}>
          GA4 follows the property’s timezone. Search Console uses Pacific time,
          so daily intervals can differ between sources.
        </p>
      </section>
      <section className={styles.panel}>
        <div className={styles.panelHeading}>
          <div>
            <h2>Google account</h2>
            <p>
              {account.isPending
                ? "Checking connection…"
                : account.isError
                  ? "Account connection could not be checked"
                  : account.data?.connected
                    ? "Google access is available. Review the selected account on each source above."
                    : "Connect an account to choose reporting resources."}
            </p>
          </div>
          {canManage && (
            <Button
              variant="secondary"
              onClick={connect}
              loading={connecting}
              icon={<ExternalLink size={15} />}
            >
              {account.data?.connected ? "Reconnect account" : "Connect Google"}
            </Button>
          )}
        </div>
        <p className={styles.caption}>
          Reconnecting updates access for this account. Changing a website
          source above affects only that website’s reports.
        </p>
        {error && <p role="alert">{error}</p>}
      </section>
      {editing && (
        <BindingDialog
          scope={scope}
          website={website}
          source={editing}
          currentResourceLabel={context.sources[editing].resource_scope}
          onConnectGoogle={connect}
          connecting={connecting}
          connectError={error}
          onClose={() => setEditing(null)}
        />
      )}
      <span className={styles.srOnly}>Website {id}</span>
    </div>
  );
}
